/**
 * Sigla product-card swatches.
 *
 * <sigla-card-swatches> wraps the thumbnails under a product card. Hovering (mouse) or
 * tapping/clicking (touch, keyboard) a swatch previews that variant on the card: image,
 * links and the quick-add variant id. Defined as a custom element so every card sets
 * itself up when it is added to the page (including infinite scroll and the theme
 * editor) and cleans up when removed. No page-wide observers.
 */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class SiglaCardSwatches extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.track = this.querySelector('[data-sigla-swatches-track]');
    this.nextButton = this.querySelector('[data-sigla-swatches-next]');
    this.card = this.closest('product-card');

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('pointerover', this.#onPointerOver, { signal });
    this.addEventListener('focusin', this.#onFocus, { signal });

    if (this.track && 'ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.#updateOverflow());
      this.resizeObserver.observe(this.track);
    }
    this.#updateOverflow();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.resizeObserver?.disconnect();
  }

  #onClick = (event) => {
    if (event.target.closest('[data-sigla-swatches-next]')) {
      event.preventDefault();
      event.stopPropagation();
      this.#scrollNext();
      return;
    }
    const swatch = event.target.closest('[data-sigla-card-swatch]');
    if (!swatch) return;
    // Keep the click from reaching the card's full-size link underneath.
    event.preventDefault();
    event.stopPropagation();
    this.select(swatch);
  };

  #onPointerOver = (event) => {
    if (event.pointerType !== 'mouse') return;
    const swatch = event.target.closest('[data-sigla-card-swatch]');
    if (swatch && !swatch.classList.contains('is-active')) this.select(swatch);
  };

  #onFocus = (event) => {
    const swatch = event.target.closest('[data-sigla-card-swatch]');
    if (swatch && event.target.matches(':focus-visible')) this.select(swatch);
  };

  #updateOverflow() {
    if (!this.track || !this.nextButton) return;
    const overflowing = this.track.scrollWidth > this.track.clientWidth + 3;
    this.classList.toggle('is-overflowing', overflowing);
    this.nextButton.hidden = !overflowing;
  }

  #scrollNext() {
    const track = this.track;
    if (!track) return;
    const first = track.querySelector('[data-sigla-card-swatch]');
    const gap = parseFloat(getComputedStyle(track).columnGap) || 8;
    const step = first ? first.getBoundingClientRect().width + gap : 62;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 5;
    const behavior = reducedMotion.matches ? 'auto' : 'smooth';
    if (atEnd) track.scrollTo({ left: 0, behavior });
    else track.scrollBy({ left: step * 2, behavior });
  }

  #mainImage() {
    const card = this.card;
    if (!card) return null;
    return (
      card.querySelector('slideshow-slide[aria-hidden="false"] .product-media__image') ||
      card.querySelector('slideshow-slide:first-of-type .product-media__image') ||
      card.querySelector('.product-media__image')
    );
  }

  async select(swatch) {
    const { variantId, variantUrl, imageSrc, imageSrcset } = swatch.dataset;
    const card = this.card;
    if (!card) return;

    this.querySelectorAll('[data-sigla-card-swatch]').forEach((button) => {
      const active = button === swatch;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    if (variantUrl) {
      card.querySelectorAll('[ref="productCardLink"], [data-sigla-card-action-link]').forEach((link) => {
        link.href = variantUrl;
      });
    }

    if (variantId) {
      card.dataset.selectedVariantId = variantId;
      card.querySelectorAll('input[name="id"]').forEach((input) => {
        input.value = variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    const image = this.#mainImage();
    if (!image || !imageSrc || image.dataset.siglaSrc === imageSrc) return;

    // Load the new image off-screen first so the card never flashes blank.
    const preload = new Image();
    preload.sizes = image.sizes;
    if (imageSrcset) preload.srcset = imageSrcset;
    preload.src = imageSrc;
    try {
      await preload.decode();
    } catch {
      /* show it anyway */
    }
    if (!swatch.classList.contains('is-active')) return; // user moved on
    image.dataset.siglaSrc = imageSrc;
    if (imageSrcset) image.srcset = imageSrcset;
    else image.removeAttribute('srcset');
    image.src = imageSrc;
    image.closest('picture')?.querySelectorAll('source').forEach((source) => source.remove());
    card.dataset.featuredMediaUrl = imageSrc;
  }
}

if (!customElements.get('sigla-card-swatches')) {
  customElements.define('sigla-card-swatches', SiglaCardSwatches);
}
